<template>
  <div class="eval-card">
    <h6>{{ titleSlug }}</h6>
    
    <!-- Show loading state while data is being processed -->
    <div v-if="isLoading" class="loading-state">
      <p>Calculating metrics...</p>
    </div>
    
    <!-- Show error state if data is invalid -->
    <div v-else-if="hasDataError" class="error-state">
      <p class="error-message">{{ errorMessage }}</p>
    </div>
    
    <!-- Show metrics only when data is valid -->
    <div v-else-if="hasValidData">
      <ul v-if="Object.keys(metrics).length">
        <li> 
          MSE (Mean Squared Error): <span class="result-value">{{ formatNumber(metrics.mse) }}</span>
        </li>
        <li> 
          MAE (Mean Absolute Error): <span class="result-value">{{ formatNumber(metrics.mae) }}</span>
        </li>
        <li> 
          MAPE (Mean Absolute Percentage Error): <span class="result-value">
          {{ isFinite(metrics.mape) ? formatNumber(metrics.mape) + '%' : 'N/A' }} 
          <span v-if="isFinite(metrics.mape)" :class="getBenchmarkClass('mape', metrics.mape)">
            {{ getBenchmarkMessage('mape', metrics.mape) }}
          </span></span>
        </li>
        <li> 
          RMSE (Root Mean Squared Error): <span class="result-value">{{ formatNumber(metrics.rmse) }}</span>
        </li>
        <li> 
          NRMSE (Normalized RMSE): <span class="result-value">{{ formatNumber(metrics.nrmse) }}
          <span :class="getBenchmarkClass('nrmse', metrics.nrmse)">
            {{ getBenchmarkMessage('nrmse', metrics.nrmse) }}
          </span></span>
        </li>
        <li> 
          σ Sigma (Standard Deviation of Residuals): <span class="result-value">{{ formatNumber(metrics.sigma) }}</span>
        </li>
        <li> 
          CE (Coefficient of Efficiency): <span class="result-value">{{ formatNumber(metrics.ce) }}
          <span :class="getBenchmarkClass('ce', metrics.ce)">
            {{ getBenchmarkMessage('ce', metrics.ce) }}
          </span></span>
        </li>
        <li> 
          CRPS (Continuous Ranked Probability Score): <span class="result-value">{{ formatNumber(metrics.crps) }}</span>
        </li>
      </ul>

      <p>Autocorrelation Plot</p>
      <div ref="autocorrelationPlot"></div>
      
      <p>Partial Autocorrelation Plot</p>
      <div ref="partialAutocorrelationPlot"></div>
    </div>
    
    <!-- Show message when no data is available -->
    <div v-else class="no-data-state">
      <p>No data available for evaluation.</p>
    </div>
  </div>
</template>

<script>
import * as math from "mathjs";
import Plotly from "plotly.js-basic-dist";

export default {
  props: {
    actual: {
      type: Array,
      default: () => []
    },
    predicted: {
      type: Array,
      default: () => []
    },
    titleSlug: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      metrics: {},
      isLoading: false,
      hasDataError: false,
      errorMessage: '',
    };
  },
  computed: {
    hasValidData() {
      return this.isValidArray(this.actual) && 
             this.isValidArray(this.predicted) && 
             !this.hasDataError;
    },
    
    processedData() {
      if (!this.hasValidData) return { actual: [], predicted: [] };
      
      const actualData = this.convertToNumbers(this.actual);
      const predictedData = this.convertToNumbers(this.predicted);
      
      // Handle length mismatch by truncating to the shorter length
      const minLength = Math.min(actualData.length, predictedData.length);
      
      if (actualData.length !== predictedData.length) {
        // console.warn(`Array length mismatch in ${this.titleSlug}: actual=${actualData.length}, predicted=${predictedData.length}. Truncating to ${minLength}.`);
      }
      
      return {
        actual: actualData.slice(0, minLength),
        predicted: predictedData.slice(0, minLength)
      };
    }
  },
  watch: {
    actual: {
      handler() {
        this.validateAndCalculate();
      },
      immediate: true
    },
    predicted: {
      handler() {
        this.validateAndCalculate();
      },
      immediate: true
    },
  },
  mounted() {
    this.validateAndCalculate();
  },
  methods: {
    convertToNumbers(arr) {
      if (!Array.isArray(arr)) return [];
      return arr.map(val => {
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
          const num = parseFloat(val);
          return isNaN(num) ? null : num;
        }
        return null;
      }).filter(val => val !== null);
    },
    
    isValidArray(arr) {
      if (!Array.isArray(arr) || arr.length === 0) return false;
      
      // Convert to numbers first
      const numbers = this.convertToNumbers(arr);
      
      // Check if we have valid numbers after conversion
      return numbers.length > 0 && numbers.every(val => typeof val === 'number' && isFinite(val));
    },
    
    validateAndCalculate() {
      try {
        this.isLoading = true;
        this.hasDataError = false;
        this.errorMessage = '';
        
        // Reset metrics
        this.metrics = {};
        
        // Validate input data
        if (!this.isValidArray(this.actual)) {
          if (this.actual && this.actual.length > 0) {
            this.hasDataError = true;
            this.errorMessage = 'Invalid actual data: contains non-numeric values';
            return;
          }
          // If actual is empty, just don't show anything (not an error)
          return;
        }
        
        if (!this.isValidArray(this.predicted)) {
          if (this.predicted && this.predicted.length > 0) {
            this.hasDataError = true;
            this.errorMessage = 'Invalid predicted data: contains non-numeric values';
            return;
          }
          // If predicted is empty, just don't show anything (not an error)
          return;
        }
        
        // Check if we have sufficient data
        const { actual, predicted } = this.processedData;
        if (actual.length < 2 || predicted.length < 2) {
          this.hasDataError = true;
          this.errorMessage = 'Insufficient data points for meaningful analysis (minimum 2 required)';
          return;
        }
        
        // Calculate metrics
        this.calculateMetrics(actual, predicted);
        
      } catch (error) {
        // console.error('Error in AccuracyEval calculation:', error);
        this.hasDataError = true;
        this.errorMessage = 'Error calculating metrics: ' + error.message;
      } finally {
        this.isLoading = false;
      }
    },

    calculateMetrics(actual, predicted) {
      try {
        const n = actual.length;
        
        // Basic error metrics
        const mse = math.mean(actual.map((val, i) => Math.pow(val - predicted[i], 2)));
        const mae = math.mean(actual.map((val, i) => Math.abs(val - predicted[i])));
        
        // MAPE calculation with proper handling of zero values
        const valid_mape_values = actual
          .map((val, i) => (Math.abs(val) > 1e-10 ? (Math.abs(val - predicted[i]) / Math.abs(val)) * 100 : null))
          .filter((x) => x !== null && isFinite(x));

        const mape = valid_mape_values.length > 0 ? math.mean(valid_mape_values) : NaN;
        
        // Other metrics
        const rmse = Math.sqrt(mse);
        const actualRange = math.max(actual) - math.min(actual);
        const nrmse = actualRange > 0 ? rmse / actualRange : NaN;
        
        const residuals = actual.map((val, i) => val - predicted[i]);
        const sigma = residuals.length > 1 ? math.std(residuals) : 0;
        
        const meanActual = math.mean(actual);
        const ssRes = math.sum(actual.map((val, i) => Math.pow(val - predicted[i], 2)));
        const ssTot = math.sum(actual.map((val) => Math.pow(val - meanActual, 2)));
        const ce = ssTot > 0 ? 1 - (ssRes / ssTot) : NaN;
        
        const crps = math.mean(actual.map((val, i) => {
          const denominator = Math.pow(Math.abs(val) + 1, 2);
          return Math.pow(val - predicted[i], 2) / denominator;
        }));

        this.metrics = { mse, mae, mape, rmse, nrmse, sigma, ce, crps };
        
        // Plot autocorrelation functions
        this.$nextTick(() => {
          if (residuals.length > 2) {
            this.plotAutocorrelation(residuals);
            this.plotPartialAutocorrelation(residuals);
          }
        });
        
      } catch (error) {
        // console.error('Error calculating metrics:', error);
        throw error;
      }
    },

    plotAutocorrelation(series) {
      try {
        if (!this.$refs.autocorrelationPlot) return;
        
        const maxLag = Math.min(series.length - 1, 20); // Limit lags to prevent performance issues
        let lags = [...Array(maxLag).keys()];
        let autocorr = lags.map((lag) => this.autoCorrelation(series, lag));

        let trace = {
          x: lags,
          y: autocorr,
          mode: "lines+markers",
          type: "scatter",
        };

        let layout = { 
          title: "Autocorrelation Plot", 
          xaxis: { title: "Lag" }, 
          yaxis: { title: "ACF" },
          height: 300
        };

        Plotly.purge(this.$refs.autocorrelationPlot);
        Plotly.newPlot(this.$refs.autocorrelationPlot, [trace], layout);
      } catch (error) {
        // console.error('Error plotting autocorrelation:', error);
      }
    },

    plotPartialAutocorrelation(series) {
      try {
        if (!this.$refs.partialAutocorrelationPlot) return;
        
        const maxLag = Math.min(series.length - 1, 20); // Limit lags to prevent performance issues
        let lags = [...Array(maxLag).keys()];
        let pacf = lags.map((lag) => this.partialAutoCorrelation(series, lag));

        let trace = {
          x: lags,
          y: pacf,
          mode: "lines+markers",
          type: "scatter",
        };

        let layout = { 
          title: "Partial Autocorrelation Plot", 
          xaxis: { title: "Lag" }, 
          yaxis: { title: "PACF" },
          height: 300
        };

        Plotly.purge(this.$refs.partialAutocorrelationPlot);
        Plotly.newPlot(this.$refs.partialAutocorrelationPlot, [trace], layout);
      } catch (error) {
        // console.error('Error plotting partial autocorrelation:', error);
      }
    },

    autoCorrelation(series, lag) {
      if (lag >= series.length) return 0;
      
      let mean = math.mean(series);
      let numerator = math.sum(series.slice(lag).map((val, i) => (val - mean) * (series[i] - mean)));
      let denominator = math.sum(series.map((val) => Math.pow(val - mean, 2)));
      
      return denominator > 0 ? numerator / denominator : 0;
    },

    partialAutoCorrelation(series, lag) {
      if (lag === 0) return 1;
      if (lag >= series.length) return 0;
      
      // Simplified PACF calculation
      return this.autoCorrelation(series, lag) - (lag > 1 ? this.autoCorrelation(series, lag - 1) : 0);
    },

    formatNumber(value) {
      if (value === undefined || value === null || !isFinite(value)) return 'N/A';
      return value.toFixed(2);
    },

    getBenchmarkMessage(metric, value) {
      if (!isFinite(value)) return '';
      
      if (metric === "mape") {
        if (value <= 10) return "(Excellent)";
        if (value <= 20) return "(Good)";
        if (value <= 50) return "(Fair)";
        return "(Poor)";
      } else if (metric === "nrmse") {
        if (value <= 0.1) return "(Excellent)";
        if (value <= 0.2) return "(Good)";
        return "(Needs Improvement)";
      } else if (metric === "ce") {
        if (value >= 0.9) return "(Excellent)";
        if (value >= 0.7) return "(Good)";
        if (value >= 0.5) return "(Fair)";
        return "(Poor)";
      }
      return "";
    },

    getBenchmarkClass(metric, value) {
      if (!isFinite(value)) return "";
      
      if (metric === "mape") {
        if (value <= 10) return "text-green-500";
        if (value <= 20) return "text-blue-500";
        if (value <= 50) return "text-yellow-500";
        return "text-red-500";
      } else if (metric === "nrmse") {
        if (value <= 0.1) return "text-green-500";
        if (value <= 0.2) return "text-blue-500";
        return "text-red-500";
      } else if (metric === "ce") {
        if (value >= 0.9) return "text-green-500";
        if (value >= 0.7) return "text-blue-500";
        if (value >= 0.5) return "text-yellow-500";
        return "text-red-500";
      }
      return "";
    },
  },
};
</script>

<style scoped>
.eval-card {
  margin: 20px;
  padding: 20px;
  border: 1px solid #85ee89;
  border-radius: 5px;
}

.eval-card li {
  font-size: 0.7rem;
}

.result-value {
  font-weight: bold;
}

.loading-state {
  text-align: center;
  padding: 20px;
  color: var(--text-tertiary);
}

.error-state {
  text-align: center;
  padding: 20px;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
}

.error-message {
  color: var(--text-primary);
  font-weight: bold;
  margin: 0;
}

.no-data-state {
  text-align: center;
  padding: 20px;
  color: var(--text-tertiary);
  font-style: italic;
}
</style>